import React, { useMemo } from 'react';
import { Button, Collapse, ErrorHandler, Gap, Text } from '@common/components';
import { useMutation, useParams, useQuery } from '@common/hooks';
import { Spacing } from '@common/tokens';
import { SCHEDULE_HEALTH_SNAPSHOT_QUERY } from '@load-balancing/graphql/queries/schedule-health-snapshot';
import { RECOMPUTE_SCHEDULE_REVIEW_KPIS_MUTATION } from '@load-balancing/graphql/mutations/recompute-schedule-review-kpis';
import { KpiChip, KpiRowType, KpiTable } from './_schedule-health-strip-kpis';

const KPI_DEFINITIONS: ReadonlyArray<
  Pick<KpiRowType, 'key' | 'label' | 'tooltip'>
> = [
  {
    key: 'fillRate',
    label: 'Fill Rate',
    tooltip:
      'Percentage of required shifts that are filled. 100 means every shift has coverage.',
  },
  {
    key: 'fteUtilization',
    label: 'FTE Utilization',
    tooltip:
      'How closely scheduled hours match contracted hours. Lower means workers are under their target.',
  },
  {
    key: 'preferenceMatch',
    label: 'Preference Match',
    tooltip:
      'Percentage of self-serviced shifts the worker kept in the current schedule.',
  },
  {
    key: 'loadBalance',
    label: 'Load Balance',
    tooltip:
      'How evenly staffing is distributed across days. 100 means perfectly balanced.',
  },
];

export function ScheduleHealthStrip() {
  const { runId = '', organizationUnitId = '' } = useParams<{
    runId: string;
    organizationUnitId: string;
  }>();
  const { data, error, refetch } = useQuery(SCHEDULE_HEALTH_SNAPSHOT_QUERY, {
    variables: { balancerRunId: runId, organizationUnitId },
    skip: !runId || !organizationUnitId,
  });
  const [recompute, { loading: recomputing }] = useMutation(
    RECOMPUTE_SCHEDULE_REVIEW_KPIS_MUTATION
  );

  // TODO(WORK-6374): temporary. Until post-run moves recompute the "Current" moment whenever they
  // change the output snapshot, this button is the only way to refresh it — force the recompute,
  // then re-read the freshly persisted moment. Recompute re-persists BOTH the strip's "Current"
  // scalars and the Team tab's per-worker FTE, so refresh both queries: the page's `ScheduleReview`
  // query (Team tab roster) via refetchQueries, and this strip's own snapshot query via refetch.
  const onRecompute = async () => {
    if (!runId || !organizationUnitId) return;
    await recompute({
      variables: { balancerRunId: runId, organizationUnitId },
      refetchQueries: ['ScheduleReview'],
      awaitRefetchQueries: true,
    });
    await refetch();
  };
  const summary = data?.scheduleReview?.snapshotSummary;
  const preBalance = summary?.preBalance;
  const autoBalanced = summary?.autoBalanced;
  const current = data?.scheduleReview?.currentSummary;

  const kpiRows = useMemo<KpiRowType[]>(
    () =>
      KPI_DEFINITIONS.map((def) => ({
        ...def,
        pre: preBalance?.[def.key] ?? null,
        balanced: autoBalanced?.[def.key] ?? null,
        // Server-recomputed live "current" value; falls back to auto_balanced if absent so the
        // column never goes blank. The "Your edits" delta (current − balanced) is derived in
        // the KPI row component.
        current: current?.[def.key] ?? autoBalanced?.[def.key] ?? null,
      })),
    [preBalance, autoBalanced, current]
  );

  if (error) {
    return (
      <ErrorHandler
        error={error}
        log="ScheduleHealthStrip"
        messageType="contextual"
      />
    );
  }

  return (
    // The strip's flex layout (nowrap title, wrapping KPI summary) is provided
    // by Collapse.Panel's `strip` kind, so this component composes primitives
    // rather than restyling the header band. SideShell keeps this fixed-height
    // band from stretching (only its last main child fills the column).
    <div className="schedule-health-strip">
      <Collapse>
        <Collapse.Panel
          panelKey="schedule-health"
          kind="strip"
          title={
            <Gap size="sm" align="baseline">
              <Text size="sm" bold color="midnightDarkest">
                Schedule Health
              </Text>
              <Text size="sm" color="midnightLighter">
                All weeks &#8729; All roles
              </Text>
            </Gap>
          }
          summary={kpiRows.map((row) => (
            <KpiChip key={row.key} row={row} />
          ))}
        >
          <KpiTable rows={kpiRows} />
          {/* TODO(WORK-6374): temporary force-recompute control — overlaid on the strip's right
          edge (left of the chevron). Remove once post-run moves refresh the "Current" moment. */}
          <div className="schedule-health-strip__recompute">
            <Button
              kind="secondary"
              size="sm"
              loading={recomputing}
              onClick={() => {
                void onRecompute();
              }}
            >
              Recompute
            </Button>
          </div>
        </Collapse.Panel>
      </Collapse>
      <style jsx>{`
        .schedule-health-strip {
          position: relative;
        }
        .schedule-health-strip__recompute {
          display: flex;
          justify-content: flex-end;
          margin-top: ${Spacing.sm}px;
        }
      `}</style>
    </div>
  );
}
